import { useEffect, useRef, useState } from "react";

import { getImageUrl } from "@/utils/GetImageUrl";
import { ImageType } from "@/utils/shared-atom-types";
import { Image } from "@/components/atoms/Image";
import { Text } from "@/components/atoms/Text";
import { useClassNames } from "@/utils/classNames";
import { RatingsBadge } from "@/components/atoms/RatingsBadge";
import { RatingsBadgeVariant } from "@/components/atoms/RatingsBadge/types";
import { VideoPlayer } from "@/components/organisms/VideoPlayer";
import { getCantMissHighlightsInfo } from "@/utils/GetCantMissHighlightsInfo";
import { getCantMissClipsInfo, getNBAClip } from "@/utils/GetCantMissClipsInfo";
import { isStory } from "@/utils/FetchAssetDetails/types/type-guards";
import { CoreBadge } from "@/components/atoms/CoreBadge";
import {
  CoreBadgeSize,
  CoreBadgeVariant,
} from "@/components/atoms/CoreBadge/types";
import { useScrollPosition } from "@/utils/hooks/UseScrollPosition";

import styles from "./ClipsTile.module.scss";
import { ClipsTileProps, ClipsVariant } from "./types";

/**
 * ClipsTile component renders a clip tile for displaying Can't Miss Clips and Highlights.
 *
 * @param asset - The asset data to be displayed in the tile
 * @param variant - The variant of the clip tile, either "clips" for series clips or "highlights" for NBA clips
 * @param index - The index of the tile within its rail, used for selecting NBA highlight clips
 * @param className - Additional CSS class for styling
 *
 * @returns the rendered ClipsTile component
 *
 * @example
 * ```tsx
 * <ClipsTile asset={someAsset} variant={ClipsVariant.Clips} tileContainerRef={tileContainerRef} index={0} />
 * ```
 */
const ClipsTile = ({
  asset,
  variant = ClipsVariant.Clips,
  index,
  delay,
  visibilityThreshold = 0.8,
  tileContainerRef,
  scrollContainerRef,
  className,
}: ClipsTileProps) => {
  const cn = useClassNames(styles, styles["clips-tile"], className);

  const tileRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isAutoplaying, setIsAutoplaying] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scrollY = useScrollPosition(scrollContainerRef);

  /* Determine if the tile is autoplaying based on scroll position */
  useEffect(() => {
    const currentTile = tileRef.current;
    if (!currentTile) return;

    const tileContainer =
      tileContainerRef && "current" in tileContainerRef
        ? tileContainerRef.current
        : null;
    if (!tileContainer) return;

    const scrollContainer = scrollContainerRef?.current;
    const PRELOAD_RANGE = tileContainer.childElementCount; // Change this value to only preload tiles in this range

    const isTileAutoplaying = () => {
      const children = Array.from(tileContainer.children) as HTMLElement[];
      if (!children.length) return;

      const target = tileContainer.scrollLeft;
      let autoplayIndex = 0;
      let bestDist = Number.POSITIVE_INFINITY;

      children.forEach((child, idx) => {
        const start = child.offsetLeft;
        const dist = Math.abs(start - target);
        if (dist < bestDist) {
          bestDist = dist;
          autoplayIndex = idx;
        }
      });

      const currentTileIndex = children.indexOf(currentTile);
      const shouldAutoplay = currentTileIndex === autoplayIndex;

      /** Check if the rail container is in the viewport */
      const railRect = tileContainer.getBoundingClientRect();
      const containerRect = scrollContainer?.getBoundingClientRect();

      if (!containerRect) {
        // Gracefully handle missing container by stopping autoplay
        setShouldLoadVideo(false);
        setIsAutoplaying(false);
        if (autoplayTimerRef.current) {
          clearTimeout(autoplayTimerRef.current);
          autoplayTimerRef.current = null;
        }
        return;
      }

      const viewportHeight = containerRect.height;
      const viewportTop = containerRect.top;

      // Calculate rail position relative to the scroll container viewport
      const railTop = railRect.top;
      const railBottom = railRect.bottom;
      const railHeight = railRect.height;

      // Calculate visible portion of rail within the scroll container
      const visibleTop = Math.max(railTop, viewportTop);
      const visibleBottom = Math.min(railBottom, viewportTop + viewportHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityRatio = visibleHeight / railHeight;

      const isInVerticalView = visibilityRatio >= visibilityThreshold;

      /** Check if this tile is within preload range */
      const distanceFromActive = Math.abs(currentTileIndex - autoplayIndex);
      const shouldLoad = distanceFromActive <= PRELOAD_RANGE;

      setShouldLoadVideo(shouldLoad);

      /** Clear existing timer */
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
      }

      if (shouldAutoplay && isInVerticalView) {
        // Start autoplay after delay
        autoplayTimerRef.current = setTimeout(() => {
          setIsAutoplaying(true);
        }, delay);
      } else {
        // Stop immediately when not focused
        setIsAutoplaying(false);
      }
    };

    isTileAutoplaying();
    const onHorizontalScroll = () => {
      requestAnimationFrame(isTileAutoplaying);
    };

    tileContainer.addEventListener("scroll", onHorizontalScroll);

    return () => {
      tileContainer.removeEventListener("scroll", onHorizontalScroll);
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
      }
    };
  }, [scrollY, tileContainerRef, scrollContainerRef]);

  /* Control video playback when isAutoplaying changes  */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoadVideo) return;

    if (isAutoplaying) {
      video.play();
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [isAutoplaying, shouldLoadVideo]);

  useEffect(() => {
    if (!shouldLoadVideo && videoRef.current) {
      videoRef.current = null;
    }
  }, [shouldLoadVideo]);

  /* Early returns for invalid states */
  if (!asset) {
    return null;
  }

  /* If the asset is a story, check if it's eventStage upcoming or replay. If upcoming, hide this tile. If replay, show the replay badge. */
  const isUpcoming =
    isStory(asset) && asset.attributes.eventDetails.eventStage === "UPCOMING";
  if (isUpcoming) {
    return null;
  }

  const isReplay =
    isStory(asset) && asset.attributes.eventDetails.eventStage === "REPLAY";

  /* Get image URL with fallback */
  const imageUrl =
    getImageUrl(asset, ImageType.Scene34, { width: 255, height: 300 }) ||
    getImageUrl(asset, ImageType.TitleArt34, { width: 255, height: 300 });

  /* Get video URL based on variant */
  const clipInfo = getCantMissClipsInfo(asset);
  const videoUrl =
    variant === ClipsVariant.Clips
      ? clipInfo.url
      : getNBAClip((index ?? 0) % 4);

  const title = asset.attributes.title;

  /* Get marketing copy and rating for Can't Miss Clips */
  const rating = clipInfo.ratingBadge;
  let topLine = clipInfo.topLineText;
  let bottomLine = clipInfo.bottomLineText;
  let textColor = clipInfo.textColor;

  /* Override marketing copy for Can't Miss Highlights */
  if (variant === ClipsVariant.Highlights) {
    const teamNames = getCantMissHighlightsInfo(title ?? "");
    topLine = teamNames[0][0];
    bottomLine = teamNames[0][1];
    textColor = teamNames[1];
  }

  return (
    <div ref={tileRef} className={cn}>
      {shouldLoadVideo && (
        <VideoPlayer
          key={asset.id}
          className={styles["clips-video"]}
          isModal={false}
          showControls={false}
          loopStart={0}
          loopEnd={6}
          videoProps={{
            cloudflare: false,
            src: videoUrl,
            controls: false,
            muted: true,
            playsInline: true,
            preload: "metadata",
            onLoadedMetadata: (e) => {
              videoRef.current = e.currentTarget;
            },
          }}
        />
      )}
      <div className={styles["clip-gradient"]} />
      <div className={styles["video-overlay"]}>
        {variant === ClipsVariant.Clips && (
          <RatingsBadge
            variant={RatingsBadgeVariant.Secondary}
            className={styles["clips-rating"]}
          >
            <Text className={styles["clips-rating-text"]}>{rating}</Text>
          </RatingsBadge>
        )}
        {isReplay && (
          <CoreBadge
            className={styles["clips-badge"]}
            variant={CoreBadgeVariant.White}
            size={CoreBadgeSize.Size16}
          >
            Replay
          </CoreBadge>
        )}
        <div className={styles["marketing-copy"]}>
          <Text
            className={styles["marketing-copy-top-line"]}
            style={{ color: textColor }}
          >
            {topLine}
          </Text>
          <Text className={styles["marketing-copy-bottom-line"]}>
            {bottomLine}
          </Text>
        </div>
        <div className={styles["clips-metadata"]}>
          <Image className={styles["clips-image"]} src={imageUrl} />
          <div className={styles["clips-text-container"]}>
            <Text className={styles["clips-title"]}> {title} </Text>
            <Text className={styles["clips-updated"]}> Recently Updated </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ClipsTile };
